import _ from 'lodash';
import React from 'react';
import { useMutation } from 'react-relay';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import {
    composerQueriesCreateDesignTemplateMutation,
    DesignInput,
    DesignItemConstraintsInput
} from '../__generated__/composerQueriesCreateDesignTemplateMutation.graphql';
import { composerQueriesUpdateDesignTemplateMutation } from '../__generated__/composerQueriesUpdateDesignTemplateMutation.graphql';
import { composerCreateDesignTemplateMutation, composerUpdateDesignTemplateMutation } from '../composerQueries';
import { getDesignCollection, getDesignValue, toPlainValue } from './designCollectionMapping';
import { reconcileComposerDesign } from './reconcileComposerDesign';
import { isValidDesignPrintingMethod } from './composerDesignPrintingMethod';
import { T } from 'src/shared/helpers';

// Save design
const useSaveDesign = () => {
    const [zkProduct] = usePreview3DStore((store) => [store.zkProduct]);

    const [createDesign] = useMutation<composerQueriesCreateDesignTemplateMutation>(composerCreateDesignTemplateMutation);
    const [updateDesign] = useMutation<composerQueriesUpdateDesignTemplateMutation>(composerUpdateDesignTemplateMutation);
    const [isDesignSaving, setIsDesignSaving] = React.useState(false);

    const mapDesignToInput = (design: MPlaza.Design): DesignInput => ({
        compoundKey: getDesignValue(design, 'compoundKey'),
        currentModificationID: getDesignValue(design, 'currentModificationID'),
        designID: design.get('designID') ?? 0,
        docID: design.get('docID') ?? undefined,
        modelID: design.get('modelID'),
        modelName: design.get('modelName') ?? undefined,
        modelCode: getDesignValue<string>(design, 'modelCode') ?? zkProduct?.get('code') ?? undefined,
        pricingModelID: design.get('pricingModelID'),
        modelPriceDeltaPerc: design.get('modelPriceDeltaPerc'),
        modelPriceDeltaValue: design.get('modelPriceDeltaValue'),
        modelPrice: design.get('modelPrice'),
        canBeSetAsDefaultTemplate: getDesignValue<boolean>(design, 'canBeSetAsDefaultTemplate') ?? false,
        modelTextBasePrice: design.get('modelTextBasePrice'),
        modelImageBasePrice: design.get('modelImageBasePrice'),
        printTypeID: design.get('printTypeID'),
        description: design.get('description') ?? undefined,
        name: design.get('name') ?? undefined,
        isTemplate: design.get('isTemplate'),
        isDraft: design.get('isDraft'),
        templateDesignID: design.get('templateDesignID'),
        customerID: design.get('customerID'),
        customerCode: getDesignValue(design, 'customerCode'),
        visitorID: design.get('visitorID'),
        visitorCode: getDesignValue(design, 'visitorCode'),
        finalPrice: design.get('finalPrice'),
        tempPreviewImageData: design.get('tempPreviewImageData') ?? undefined,
        tempPreviewImageUrl: design.get('tempPreviewImageUrl') ?? undefined,
        canAddText: design.get('canAddText'),
        canAddImage: design.get('canAddImage'),
        canAddShape: getDesignValue<boolean>(design, 'canAddShape') ?? true,
        maxNrTexts: design.get('maxNrTexts') ?? undefined,
        maxNrImages: design.get('maxNrImages') ?? undefined,
        maxNrShapes: getDesignValue(design, 'maxNrShapes'),
        filterCode: design.get('filterCode') ?? undefined,
        canChangeSvgColors: design.get('canChangeSvgColors'),
        canMaskImages: getDesignValue<boolean>(design, 'canMaskImages') ?? false,
        uploadRestrictions: design.get('uploadRestrictions') ? toPlainValue(design.get('uploadRestrictions')!) : undefined,
        disableSellerImages: design.get('disableSellerImages') ?? false,
        hasSyncEnabled: design.get('hasSyncEnabled'),
        salesChannelID: getDesignValue<number>(design, 'salesChannelID') ?? 0,
        createdBy: getDesignValue<number>(design, 'createdBy') ?? 0,
        createdOn: getDesignValue(design, 'createdOn'),
        modifiedBy: getDesignValue<number>(design, 'modifiedBy') ?? 0,
        modifiedOn: getDesignValue(design, 'modifiedOn'),
        isDeleted: getDesignValue<boolean>(design, 'isDeleted') ?? false,
        isLibrary: getDesignValue<boolean>(design, 'isLibrary') ?? false,
        compositionID: getDesignValue<number>(design, 'compositionID') ?? 0,
        compositionDocID: getDesignValue(design, 'compositionDocID'),
        pdSettings: getDesignValue<number>(design, 'pdSettings') ?? 0,
        interfaceID: getDesignValue(design, 'interfaceID'),
        marketPlaceOrderID: getDesignValue(design, 'marketPlaceOrderID'),
        parentTemplateId: getDesignValue(design, 'parentTemplateId'),
        templateCode: getDesignValue(design, 'templateCode'),
        adaptItemsToVariants: getDesignValue<DesignInput['adaptItemsToVariants']>(design, 'adaptItemsToVariants') ?? 'NONE',
        sides: design.get('sides').map((side) => ({
            sideID: side.get('sideID'),
            colorID: side.get('colorID'),
            modelID: side.get('modelID'),
            fillColor: side.get('fillColor') ?? undefined,
            canAddText: side.get('canAddText'),
            canAddImage: side.get('canAddImage'),
            canAddShape: true,
            canMaskImages: false,
            maxNrTexts: side.get('maxNrTexts') ?? undefined,
            maxNrImages: side.get('maxNrImages') ?? undefined,
            maxNrShapes: undefined,
            canChangeSvgColors: side.get('canChangeSvgColors'),
            disableSellerImages: side.get('disableSellerImages'),
            uploadRestrictions: side.get('uploadRestrictions') ? toPlainValue(side.get('uploadRestrictions') as any) : undefined
        })),
        sidesPrintTypes: getDesignCollection<any>(design, 'sidesPrintTypes').map((sidePrintType) => ({
            modelID: sidePrintType.modelID,
            colorID: sidePrintType.colorID,
            sideID: sidePrintType.sideID,
            printTypeID: sidePrintType.printTypeID
        })),
        areasGrids: getDesignCollection<any>(design, 'areasGrids').map((areaGrid) => ({
            modelID: areaGrid.modelID,
            colorID: areaGrid.colorID,
            sideID: areaGrid.sideID,
            areaID: areaGrid.areaID,
            gridID: areaGrid.gridID
        })),
        areas: getDesignCollection<any>(design, 'areas').map((area) => ({
            modelID: area.modelID,
            colorID: area.colorID,
            sideID: area.sideID,
            areaID: area.areaID,
            printTypeID: area.printTypeID
        })),
        designItems: design.get('designItems').map((item) => ({
            itemID: item.get('itemID') ?? 0,
            itemGuid: item.get('itemGuid'),
            index: item.get('index'),
            name: item.get('name') ?? undefined,
            json: item.get('json') ?? undefined,
            svg: item.get('svg') ?? undefined,
            imageID: item.get('imageID') ?? undefined,
            imageType: item.get('imageType') ?? undefined,
            imageFormat: item.get('imageFormat') ?? undefined,
            imageWidth: item.get('imageWidth') ?? undefined,
            imageHeight: item.get('imageHeight') ?? undefined,
            imageDpiX: item.get('imageDpiX') ?? undefined,
            imageDpiY: item.get('imageDpiY') ?? undefined,
            imageIsMulticolor: item.get('imageIsMulticolor') ?? false,
            imagePreferredWidth: item.get('imagePreferredWidth') ?? undefined,
            imagePreferredHeight: item.get('imagePreferredHeight') ?? undefined,
            imageFileSize: item.get('imageFileSize') ?? undefined,
            imagePrice: item.get('imagePrice') ?? undefined,
            imageCustomerCode: undefined,
            imageVisitorCode: undefined,
            imageSellerID: 0,
            imageCustomerID: item.get('imageCustomerID') ?? undefined,
            imageVisitorID: item.get('imageVisitorID') ?? undefined,
            providerImageID: undefined,
            applyImagePriceForQuantity: undefined,
            itemColors: item.get('itemColors').map((ic) => {
                if (!ic) return null;
                if (ic instanceof Object === false) return ic;
                return ic.get('colorCode') ?? null;
            }),
            areas: item.get('areas').map((area) => ({
                modelID: area.get('modelID'),
                areaID: area.get('areaID'),
                sideID: area.get('sideID'),
                colorID: area.get('colorID')
            })),
            colors: item.get('colors').map((color) => ({
                colorID: color.get('colorID'),
                colorCode: color.get('colorCode') ?? undefined,
                imageID: color.get('imageID') ?? undefined
            })),
            constraints:
                item.get('constraints')?.toJSON() &&
                _.extend(_.omit<DesignItemConstraintsInput>(item.get('constraints')?.toJSON(), ['canMoveX', 'canMoveY']), {
                    isAlwaysOnBottom: false
                }),
            defaultLCID: 0,
            designItemTagType: 'NONE'
        })),

        categories: getDesignCollection<any>(design, 'categories').map((category) => ({
            categoryID: category.categoryID,
            name: category.name
        })),
        tags: design.get('tags') ?? undefined,
        clientPreviews: getDesignCollection<any>(design, 'clientPreviews').map((preview) => ({
            sideID: preview.sideID,
            fileObjectID: preview.fileObjectID,
            url: preview.url,
            name: preview.name,
            modificationID: preview.modificationID
        })),
        cameraScreenshots: getDesignCollection<any>(design, 'cameraScreenshots').map((screenshot) => ({
            cameraLocationID: screenshot.cameraLocationID,
            cameraName: screenshot.cameraName,
            sideID: screenshot.sideID,
            fileObjectID: screenshot.fileObjectID,
            url: screenshot.url,
            modificationID: screenshot.modificationID
        })),
        colors: getDesignCollection<any>(design, 'colors').map((color) => ({
            colorID: color.colorID,
            name: color.name,
            isDefault: color.isDefault
        })),
        canvasRotation: getDesignCollection<any>(design, 'canvasRotation').map((rotation) => ({
            sideID: rotation.sideID,
            degrees: rotation.degrees
        })),
        options: getDesignCollection<any>(design, 'options').map((option) => ({
            id: option.id,
            colorID: option.colorID,
            sideID: option.sideID,
            height: option.height,
            width: option.width,
            columns: option.columns,
            rows: option.rows
        })),
        modifications: getDesignCollection<any>(design, 'modifications').map((modification) => ({
            modificationID: modification.modificationID,
            modelColorID: modification.modelColorID,
            designElementsDescID: modification.designElementsDescID,
            variantNotMatched: modification.variantNotMatched
        })),
        elementsDescs: getDesignCollection<any>(design, 'elementsDescs'),
        designTempPreviews: getDesignCollection<any>(design, 'designTempPreviews').map((preview) => ({
            designTempPreviewID: preview.designTempPreviewID,
            fileObjectID: preview.fileObjectID,
            url: preview.url,
            modificationID: preview.modificationID
        })),
        designVariantCaches: getDesignCollection<any>(design, 'designVariantCaches').map((cache) => ({
            previewFileObjectID: cache.previewFileObjectID,
            designFileObjectID: cache.designFileObjectID,
            previewUrl: cache.previewUrl,
            colorID: cache.colorID
        })),
        moqs: getDesignCollection<any>(design, 'moqs'),
        additionalAttributes: getDesignCollection<any>(design, 'additionalAttributes')
    });

    const saveDesign = async (libraryDesign: MPlaza.Design) => {
        setIsDesignSaving(true);
        try {
            if (!getDesignValue<boolean>(libraryDesign, 'isDeleted') && !isValidDesignPrintingMethod(libraryDesign.get('printTypeID'))) {
                throw new Error(T._('A printing method must be selected before saving the design.', 'Admin'));
            }
            const designID = libraryDesign.getDesignID();
            const design = mapDesignToInput(libraryDesign);
            await new Promise<void>((resolve, reject) => {
                if (designID) {
                    updateDesign({
                        variables: {
                            design: design
                        },
                        onCompleted: (response, errors) => {
                            if (errors && errors.length > 0) {
                                reject(errors);
                            } else {
                                try {
                                    reconcileComposerDesign(libraryDesign, response.updateDesignTemplate);
                                } catch (error) {
                                    reject(error);
                                    return;
                                }
                                resolve();
                            }
                        },
                        onError: (err) => {
                            reject(err);
                        }
                    });
                } else {
                    createDesign({
                        variables: {
                            design: design
                        },
                        onCompleted: (response, errors) => {
                            if (errors && errors.length > 0) {
                                reject(errors);
                            } else {
                                const savedDesign = response.createDesigntemplate;
                                try {
                                    reconcileComposerDesign(libraryDesign, savedDesign);
                                } catch (error) {
                                    reject(error);
                                    return;
                                }
                                resolve();
                            }
                        },
                        onError: (err) => {
                            reject(err);
                        }
                    });
                }
            }).catch((error) => {
                throw error;
            });
            // This is upload-only data. Keeping it in the live model makes every later save
            // resend a large base64 image and makes retries look like distinct uploads.
            libraryDesign.set('tempPreviewImageData', null);
        } catch (error) {
            console.error('useSaveDesign: Error while saving design:', error);
            throw error;
        } finally {
            setIsDesignSaving(false);
        }
    };

    return [saveDesign, isDesignSaving] as const;
};

export default useSaveDesign;
